import { EMPTY, Observable, throwError } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { catchError, finalize, map, tap } from 'rxjs/operators';

import { messagingService } from './messaging.service';
import { RestfulApiService } from './restful-api.service';
import { HttpCodes } from '../constants/http-codes';
import { AuthError } from '../errors/auth-error';
import { AuthContext } from '../model/auth-context';
import { AuthTokens } from '../model/auth-tokens';
import { RestfulModel } from '../model/restful-model';
import { SecurityUtils } from '../utils/security-utils';

class AuthenticationService extends RestfulApiService<RestfulModel> {
    private inFlightRefresh?: Observable<AuthContext>;

    public login(username: string, password: string): Observable<AuthContext> {
        return this.http.post<AuthTokens | null>(this.getAbsolutePath('token'), { username, password }).pipe(
            tap((tokens) => {
                if (!tokens) {
                    throw new AuthError('No tokens in response', HttpCodes.OK);
                }
                SecurityUtils.setTokens(tokens);
            }),
            map(() => SecurityUtils.buildAuthContext()),
            catchError((err: AjaxError | AuthError) => {
                if (err.response && 'object' === typeof err.response) {
                    const resp = err.response as { message?: string };
                    if (resp.message) {
                        messagingService.showError(resp.message);

                        return EMPTY;
                    }
                }
                if (HttpCodes.GATEWAY_TIMEOUT === err.status) {
                    messagingService.showError('Request Timed Out');

                    return EMPTY;
                }

                return throwError(() => err);
            }),
        );
    }

    public refresh(refreshToken: string): Observable<AuthContext> {
        if (this.inFlightRefresh) {
            return EMPTY;
        }

        return (this.inFlightRefresh = this.http
            .post<AuthTokens>(this.getAbsolutePath('token/refresh'), { refresh_token: refreshToken })
            .pipe(
                tap((tokens) => {
                    SecurityUtils.setTokens(tokens);
                }),
                map(() => SecurityUtils.buildAuthContext()),
                finalize(() => {
                    this.inFlightRefresh = undefined;
                }),
            ));
    }

    protected getFormName(): string {
        throw new Error('This does not support crud');
    }

    protected getPath(): string {
        throw new Error('This does not support crud');
    }
}

export const authenticationService = new AuthenticationService();
